import { memo, useEffect, useRef, useState } from "react";

import { ArrowUp } from "lucide-react";

import { Image } from "@components/Image";
import { Popover, PopoverContent, PopoverTrigger } from "@components/Popover";

import { useGetEmojiListQuery } from "@storage/queries/chat/getEmojiListQuery";

import { DELAY_IMMEDIATE } from "@configs/constants";
import { ICON_PATHS } from "@configs/icons";

interface SendMessageElementProps {
	sendMessageAsync: (value: string) => Promise<void>;
	throttleUpdateDraftMessageAsync: (chatId: string, message: string) => void;
	onStartedTypingThrottle: (chatId: string | null, isMessageWasSent: boolean) => void;
	activeChat: string;
	textInputMessage: string;
	isSendMessageError: boolean;
	isNewChat: boolean;
}
export const SendMessageElement = memo(
	({
		sendMessageAsync,
		throttleUpdateDraftMessageAsync,
		onStartedTypingThrottle,
		activeChat,
		textInputMessage,
		isSendMessageError,
		isNewChat,
	}: SendMessageElementProps) => {
		const [isSending, setIsSending] = useState<boolean>(false);
		const [value, setValue] = useState<string>(textInputMessage);
		const [cursorPosition, setCursorPosition] = useState<number | null>(null);

		const inputRef = useRef<HTMLDivElement>(null);
		const emojiListQuery = useGetEmojiListQuery();

		const MAX_LENGTH_MESSAGE = 4096;
		const styleErrorTextShow = isSendMessageError ? { opacity: 1 } : { opacity: 0 };
		const sendMessageErrorStyle = isSendMessageError ? { border: "1px solid #FF5E5E" } : {};

		const saveCursorPosition = () => {
			const selection = window.getSelection();
			if (selection && selection.rangeCount > 0) {
				const range = selection.getRangeAt(0);
				const position = calculateCursorPosition(range);
				setCursorPosition(position);
			} else {
				setCursorPosition(null);
			}
		};

		const calculateCursorPosition = (range: Range) => {
			let position = 0;
			const { startContainer, startOffset } = range;

			// Traverse all nodes up to the cursor position
			const traverseNodes = (node: ChildNode) => {
				if (node === startContainer) {
					position += startOffset;
					return true;
				} else if (node.nodeType === Node.TEXT_NODE && node.textContent) {
					position += node.textContent.length;
				} else if (node.nodeType === Node.ELEMENT_NODE) {
					for (let i = 0; i < node.childNodes.length; i++) {
						if (traverseNodes(node.childNodes[i])) return true;
					}
				}
				return false;
			};

			if (inputRef.current) {
				traverseNodes(inputRef.current);
			}

			return position;
		};

		const setCursorToPosition = (element: HTMLDivElement, position: number | null) => {
			const selection = window.getSelection();
			const range = document.createRange();
			let currentPos = 0;

			if (selection && position !== null) {
				const traverseNodes = (node: ChildNode) => {
					if (node.nodeType === Node.TEXT_NODE && node.textContent) {
						const textLength = node.textContent.length;

						if (currentPos + textLength >= position) {
							range.setStart(node, position - currentPos);
							range.setEnd(node, position - currentPos);
							return true;
						}
						currentPos += textLength;
					} else if (node.nodeType === Node.ELEMENT_NODE) {
						for (let i = 0; i < node.childNodes.length; i++) {
							if (traverseNodes(node.childNodes[i])) return true;
						}
					}
					return false;
				};

				traverseNodes(element);

				selection.removeAllRanges();
				selection.addRange(range);
			}
		};

		const handleAddEmoji = (event: React.MouseEvent<HTMLSpanElement>, emojiObject: TEmojiList) => {
			event.preventDefault();
			event.stopPropagation();

			if (inputRef.current && value.length <= MAX_LENGTH_MESSAGE) {
				const currentText = inputRef.current.innerText || "";
				const emoji = emojiObject.character;

				const insertPosition = cursorPosition !== null ? cursorPosition : currentText.length;

				const updatedText = currentText.slice(0, insertPosition) + emoji + currentText.slice(insertPosition);

				inputRef.current.innerText = updatedText;
				onUpdateMessage(updatedText);

				const newCursorPosition = insertPosition + emoji.length;

				setCursorToPosition(inputRef.current, newCursorPosition);
				inputRef.current.focus();
			}
		};

		const onFinish = () => {
			onStartedTypingThrottle(activeChat, true);

			if (value.length) {
				setIsSending(true);
				sendMessageAsync(value)
					.then(() => {
						if (inputRef.current) {
							inputRef.current.innerText = "";
						}
						setValue("");
					})
					.catch(() => {})
					.finally(() => setIsSending(false));
			}
		};

		const onUpdateMessage = (message: string) => {
			setValue(message);
			onStartedTypingThrottle(activeChat, false);

			throttleUpdateDraftMessageAsync(activeChat, message.replace(/<br>/g, "\n"));
		};

		const handleMessageInput = (event: React.FormEvent<HTMLDivElement>) => {
			if (!inputRef.current) return;
			const message = (event.target as HTMLDivElement).innerText;

			let plainTextContent = inputRef.current.innerText.trim();

			if (plainTextContent.length === 0) {
				setTimeout(() => {
					onUpdateMessage("");
				}, DELAY_IMMEDIATE);
			}

			if (inputRef.current.innerText.startsWith(" ") && !plainTextContent.length) {
				plainTextContent = plainTextContent.substring(1);
				inputRef.current.innerText = plainTextContent;
			} else if (plainTextContent.length >= MAX_LENGTH_MESSAGE) {
				plainTextContent = plainTextContent.substring(0, MAX_LENGTH_MESSAGE);
				inputRef.current.innerText = plainTextContent;

				onUpdateMessage(plainTextContent);
				setCursorToEnd(inputRef.current);
			} else {
				onUpdateMessage(message);
			}
		};

		const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === "Enter" && !event.shiftKey) {
				onFinish();
				event.preventDefault();
			} else if (event.key === "Enter" && event.shiftKey) {
				document.execCommand("insertLineBreak");
				event.preventDefault();
			} else if (event.key === "Backspace" || event.key === "Delete") {
				if (!inputRef.current) return;
				if (
					(inputRef.current.innerText.length === 2 &&
						Array.from(inputRef.current.childNodes).find((node) => node.nodeName === "BR")) ||
					inputRef.current.innerText.length === 1
				) {
					onUpdateMessage("");
				}
			}
		};

		const setCursorToEnd = (element: HTMLDivElement) => {
			const range = document.createRange();
			const selection = window.getSelection();
			range.selectNodeContents(element);
			range.collapse(false);
			if (selection) {
				selection.removeAllRanges();
				selection.addRange(range);
			}
		};

		useEffect(() => {
			if (inputRef.current) {
				setValue(textInputMessage);
				inputRef.current.innerText = textInputMessage;
				setCursorToEnd(inputRef.current);
			}
		}, [textInputMessage]);

		return (
			<>
				<div className="flex flex-col px-4">
					<div className="relative flex gap-2">
						<div
							className={`relative flex max-h-50 min-h-10 w-full items-end justify-between rounded-[20px] border
								border-transparent bg-secondary px-2 py-1.25`}
							style={{ ...sendMessageErrorStyle }}
						>
							<Popover>
								<PopoverTrigger
									render={
										<button
											type="button"
											className={`mx-0.5 mb-1.25 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center
											border-none bg-transparent p-0 text-2xl`}
										>
											<Image width={20} height={20} src={ICON_PATHS.SMILE} alt="smile" className="opacity-60" />
										</button>
									}
								/>
								<PopoverContent className="h-63.75 w-75">
									{emojiListQuery.isSuccess && emojiListQuery.data?.length !== 0 ? (
										<div className="messages-box-scrollbar relative flex flex-wrap overflow-y-auto">
											{emojiListQuery.data?.map((emoji, index) => (
												<span
													role="button"
													key={index}
													onMouseDown={(e) => e.preventDefault()}
													onClick={(e) => handleAddEmoji(e, emoji)}
													className={`relative box-border inline-block py-1 pr-2 pl-1 text-center text-2xl
														transition-all duration-150 ease-[ease] hover:-translate-y-0.5 hover:cursor-pointer`}
												>
													{emoji.character}
												</span>
											))}
										</div>
									) : null}
								</PopoverContent>
							</Popover>

							<div
								className={`max-h-inherit relative flex min-h-7.5 w-[1%] flex-1 items-center self-center overflow-hidden
									px-2.25`}
							>
								<div
									className={[
										"overflow-wrap-anywhere relative max-h-50 min-h-0 w-full resize-none scrollbar-none overflow-y-auto border-none bg-transparent text-base leading-[1.3125] outline-none [[contenteditable]]:cursor-text [[contenteditable]]:bg-transparent [[contenteditable]]:whitespace-pre-wrap [[contenteditable]]:text-white [[contenteditable]]:caret-primary [[contenteditable]]:outline-none [[contenteditable]]:select-text",
										isSending && "text-msg-time!",
									]
										.filter(Boolean)
										.join(" ")}
									style={isSendMessageError ? { color: "#FF5F5F" } : {}}
									ref={inputRef}
									contentEditable
									onInput={handleMessageInput}
									onKeyDown={handleKeyDown}
									onKeyUp={saveCursorPosition}
									onMouseUp={saveCursorPosition}
								/>
								<span
									className={`pointer-events-none absolute top-1/2 left-2 z-docked max-w-full -translate-y-1/2
										overflow-hidden pe-2 text-ellipsis whitespace-nowrap text-msg-time`}
									style={value.length ? { opacity: 0 } : { opacity: 1 }}
								>
									{isNewChat ? "Make the first move..." : "Send message"}
								</span>
							</div>

							<div className="mx-0.5 mt-0 mb-1.25 w-10 flex-none p-0 text-2xl" />
							<button
								onClick={onFinish}
								disabled={isSending}
								type="submit"
								className={[
									"absolute right-0 bottom-0 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-none p-0 opacity-0",
									value.length ? "bg-primary opacity-100" : "bg-glass",
									isSending && "bg-[#6bafff]!",
								]
									.filter(Boolean)
									.join(" ")}
							>
								<div className="flex items-center justify-center">
									<ArrowUp color="white" strokeWidth={3} />
								</div>
							</button>
						</div>
					</div>

					<div className="my-1.5 flex items-center justify-center" style={{ ...styleErrorTextShow }}>
						<Image width={13} height={12} src={ICON_PATHS.MSG_ERROR} alt="msg_error" />
						<span className="ml-1.25 text-xs leading-normal font-normal text-msg-time">
							An error occurred. Please try again later.
						</span>
					</div>
				</div>
			</>
		);
	},
);

export type TEmojiList = { character: string; codePoints: string[] };
